import { useMemo } from 'react';
import { BuildingOfficeIcon, UserIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';
import { mergeNavForRoles, FOOTER_RULES } from '../../utils/roleNavMatrix';
import './AppSidebar.css';

function isActive(pathname, href) {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * The dashboard menu.
 * - variant "rail" (default): the sidebar beside the page on screens 1024 px
 *   and wider.
 * - variant "drawer": the same items inside the phone menu panel (MobileNav).
 * onNavigate runs after an item is chosen, so the panel can close (also when
 * the chosen item is the current page, which changes no route).
 */
export default function AppSidebar({ roles, variant = 'rail', onNavigate }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const navItems = useMemo(
        () => mergeNavForRoles(roles?.all ?? []),
        [roles?.all],
    );

    const showCompanySettings = (roles?.all ?? []).some(
        (r) => FOOTER_RULES.showCompanySettingsRoles.has(r),
    );

    const go = (href) => {
        navigate(href);
        if (onNavigate) onNavigate(href);
    };

    const className = variant === 'drawer' ? 'app-sidebar app-sidebar--drawer' : 'app-sidebar';

    return (
        <nav className={className} aria-label="Dashboard">
            <div className="app-sidebar-nav">
                {navItems.map((item, index) => {
                    if (item.type === 'separator') {
                        return (
                            <div
                                key={`sep-${index}`}
                                className="app-sidebar-divider"
                                aria-hidden="true"
                            />
                        );
                    }

                    const Icon = item.icon;
                    const active = isActive(pathname, item.href);

                    return (
                        <button
                            key={item.href}
                            type="button"
                            className={`app-nav-item${active ? ' active' : ''}`}
                            aria-current={active ? 'page' : undefined}
                            onClick={() => go(item.href)}
                        >
                            <Icon aria-hidden="true" />
                            <span>{item.name}</span>
                        </button>
                    );
                })}
            </div>
            <div className="app-sidebar-footer">
                <div className="app-sidebar-divider" aria-hidden="true" />
                <button
                    type="button"
                    className={`app-nav-item${isActive(pathname, '/profile') ? ' active' : ''}`}
                    aria-current={isActive(pathname, '/profile') ? 'page' : undefined}
                    onClick={() => go('/profile')}
                >
                    <UserIcon aria-hidden="true" />
                    <span>My Profile</span>
                </button>
                {showCompanySettings && (
                    <button
                        type="button"
                        className={`app-nav-item${isActive(pathname, '/company') ? ' active' : ''}`}
                        aria-current={isActive(pathname, '/company') ? 'page' : undefined}
                        onClick={() => go('/company')}
                    >
                        <BuildingOfficeIcon aria-hidden="true" />
                        <span>Company Settings</span>
                    </button>
                )}
            </div>
        </nav>
    );
}
